import { OrderDetailBottomSheet } from '@euler/app/flows/order/components/OrderDetailBottomSheet';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { ChildTaskList } from '@euler/app/flows/task/pages/detail/components/ChildTaskList';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  StoreFeature,
} from '@euler/model/enum';
import { safeMarkDevFlag } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { useIsFocused } from '@react-navigation/native';
import { memo, useCallback } from 'react';
import { SharedValue } from 'react-native-reanimated';
import { Fill } from 'react-slot-fill';
import { BehaviorSubject } from 'rxjs';
import { WiredConstructionInfo } from './ConstructionInfo';
import { WiredTaskDetailPageOrderSummary } from './Content.shared';
import { WiredDeliveryCheckInfo } from './DeliveryCheckInfo';
import { WiredFaultInspectionInfo } from './FaultInspectionInfo';
import { WiredTaskDetailFinishAction } from './FinishAction';
import { WiredInspectionInfo } from './InspectionInfo';
import { TaskDetailMenu } from './Menu';
import { WiredPreInspectionInfo } from './PreInspectionInfo';
import { TimelineSummary } from './TimelineSummary';
import { WiredRemarkSection } from './WiredRemarkSection';

const kAlwaysShowPreInspectionInfo = safeMarkDevFlag(false);
const kAlwaysShowConstructionInfo = safeMarkDevFlag(false);

export const TaskDetailPageContent = memo(
  ({
    scrollY,
    serviceRecordCount,
  }: {
    scrollY: SharedValue<number>;
    serviceRecordCount: number;
  }) => {
    const { taskNo, detail, profile, storeConfiguration } = useTaskContext();

    const basicInfo = detail.basicInfo;
    const showOrderDetail$ = useCreation(() => new BehaviorSubject(false), []);
    const canceled = basicInfo.status === InspectionTaskStatus.Cancelled;

    const onShowDetail = useCallback(() => {
      showOrderDetail$.next(true);
    }, [showOrderDetail$]);

    const isFocused = useIsFocused();

    const preInspection = useStoreFeature(StoreFeature.PreInspection);
    const shouldShowPreInspectionInfo =
      kAlwaysShowPreInspectionInfo ||
      (!detail.basicInfo.hasChildOrders &&
        // if already started, always show it
        (detail.preInspection.status === CommonTaskStatus.InProgress ||
          // if already finished, always show it
          detail.preInspection.status === CommonTaskStatus.Finished ||
          // otherwise, show it if the module is enabled and feature not disabled
          (!storeConfiguration.isPreInspectionDisabled &&
            preInspection.enabled)));

    return (
      <>
        <WiredTaskDetailPageOrderSummary
          serviceRecordCount={serviceRecordCount}
          onShowDetail={onShowDetail}
        />

        {!canceled && (
          <TaskDetailMenu serviceRecordCount={serviceRecordCount} />
        )}

        <WiredRemarkSection />

        {!canceled && shouldShowPreInspectionInfo && (
          <WiredPreInspectionInfo
            alwaysVisible={kAlwaysShowPreInspectionInfo}
          />
        )}

        {!canceled && !detail.basicInfo.hasChildOrders && (
          <WiredInspectionInfo />
        )}

        {!canceled &&
          (kAlwaysShowConstructionInfo || !detail.basicInfo.hasChildOrders) && (
            <WiredConstructionInfo />
          )}

        {!canceled && <WiredFaultInspectionInfo />}

        {!canceled && detail.basicInfo.hasChildOrders && <ChildTaskList />}

        {!canceled && <WiredDeliveryCheckInfo />}

        <TimelineSummary
          taskNo={taskNo}
          readonly={canceled}
          isIndependentDeliveryCheckEnabled={
            profile.store.isIndependentDeliveryCheckEnabled
          }
        />

        {isFocused && (
          <Fill name={`task-detail-bottom-action-${taskNo}`}>
            <WiredTaskDetailFinishAction scrollY={scrollY} />
            <OrderDetailBottomSheet visible$={showOrderDetail$} />
          </Fill>
        )}
      </>
    );
  },
);
